import React, { useState, useRef, useEffect } from "react"
import { Link } from "react-router-dom"
import { Search } from "lucide-react"

import FullScreenDropdown from "@/components/common/FullScreenDropdown"
import ProfileDropdown from "@/components/common/ProfileDropdown"
import LightDark from "@/components/common/LightDark"

import { changeSidebarVisibility, changeLeftsidebarSizeType } from "../slices/thunks"
import { useAppDispatch } from "../hooks/useAppDispatch"
import { LayoutModeTypes } from "@/enums/layout"
import { useTenant } from "@/contexts/TenantContext"

interface HeaderProps {
  onChangeLayoutMode: (mode: string) => void
  layoutModeType: LayoutModeTypes
  headerClass: string
  leftsidbarSizeType?: string
  layoutType?: string
}

const Header: React.FC<HeaderProps> = ({
  onChangeLayoutMode,
  layoutModeType,
  headerClass,
  leftsidbarSizeType = "lg",
  layoutType = "vertical",
}) => {
  const dispatch = useAppDispatch()
  const { tenant } = useTenant()
  const logoMenu = tenant?.logoMenu || import.meta.env.VITE_LOGO_MENU || ""
  const logoMenuSm = tenant?.logoMenuSm || import.meta.env.VITE_LOGO_MENU_SM || ""

  const [search, setSearch] = useState(false)
  const searchRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setSearch(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const toggleSearch = () => {
    setSearch(!search)
  }

  const toggleMenuBtn = () => {
    const windowSize = document.documentElement.clientWidth
    dispatch(changeSidebarVisibility("show"))

    if (layoutType === "horizontal") {
      if (document.body.classList.contains("menu")) {
        document.body.classList.remove("menu")
      } else {
        document.body.classList.add("menu")
      }
      return
    }

    if (layoutType === "twocolumn") {
      if (document.body.classList.contains("twocolumn-panel")) {
        document.body.classList.remove("twocolumn-panel")
      } else {
        document.body.classList.add("twocolumn-panel")
      }
      return
    }

    if (layoutType === "vertical" || layoutType === "semibox") {
      if (windowSize <= 767) {
        document.body.classList.add("vertical-sidebar-enable")
        dispatch(changeLeftsidebarSizeType("lg"))
      } else {
        document.body.classList.remove("vertical-sidebar-enable")
        const next = leftsidbarSizeType === "sm" ? "lg" : "sm"
        dispatch(changeLeftsidebarSizeType(next))
      }
    }
  }

  return (
    <header id="page-topbar" className={headerClass}>
      <div className="layout-width">
        <div className="navbar-header">
          <div className="d-flex">
            {layoutType === "horizontal" && (
              <div className="navbar-brand-box horizontal-logo">
                <Link to="/" className="logo">
                  <span className="logo-sm">
                    <img src={logoMenuSm} alt="Logo" height="22" style={{ objectFit: "contain" }} />
                  </span>
                  <span className="logo-lg">
                    <img src={logoMenu} alt="Logo" height="17" style={{ objectFit: "contain", maxWidth: "100%" }} />
                  </span>
                </Link>
              </div>
            )}

            <button
              onClick={toggleMenuBtn}
              type="button"
              className="btn btn-sm px-3 fs-16 header-item vertical-menu-btn topnav-hamburger"
              id="topnav-hamburger-icon"
            >
              <span className="hamburger-icon">
                <span></span>
                <span></span>
                <span></span>
              </span>
            </button>
          </div>

          <div className="d-flex align-items-center">
            <div
              ref={searchRef}
              className="d-md-none topbar-head-dropdown header-item"
              style={{ position: "relative" }}
            >
              <button
                type="button"
                className="btn btn-icon btn-topbar btn-ghost-secondary rounded-circle"
                onClick={toggleSearch}
              >
                <Search size={22} />
              </button>
              {search && (
                <div
                  className="dropdown-menu-lg dropdown-menu-end p-0"
                  style={{
                    position: "absolute",
                    right: 0,
                    top: "100%",
                    display: "block",
                    minWidth: 280,
                    background: "#fff",
                    border: "1px solid #e5e7eb",
                    borderRadius: 8,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                    zIndex: 1050,
                  }}
                >
                  <form className="p-3">
                    <div className="form-group m-0">
                      <div className="input-group">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Search ..."
                          aria-label="Recipient's username"
                        />
                        <button className="btn btn-primary" type="submit">
                          <Search size={16} />
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              )}
            </div>

            <FullScreenDropdown />

            <LightDark
              layoutMode={layoutModeType}
              onChangeLayoutMode={onChangeLayoutMode}
            />

            <ProfileDropdown />
          </div>
        </div>
      </div>
    </header>
  )
}

export default Header
